import React, { useEffect } from "react";
import { Stack, Box, Container, LinearProgress, Divider } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2"; // Grid version 2
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CircleIcon from '@mui/icons-material/Circle';
import { useNavigate } from "react-router-dom";
import BalanceWidget from "../widgets/account/Balance";
import ActionLinkCard from "../account/ActionLinkCard";
import ArrangementWidget from "../widgets/account/Arrangement";
import Banner from "../nav/Banner";
import SettlementOffer from "../widgets/account/SettlementOffer";
import SettlementWidget from "../widgets/account/Settlement";
import { useClient } from "../../context/ClientProvider";
import { useNav } from "../../context/NavProvider";
import { useAccount } from "../../context/AccountProvider";
import { useSfs } from "../../context/SfsProvider";
import { useArrangement } from "../../context/ArrangementProvider";
import { useNotifications } from "../../context/NotificationsProvider";
import ScriptLoader from "../account/ScriptLoader";
import SettlementProvider from "../../context/SettlementProvider";
import ArrearsCard from "../widgets/account/ArrearsCard";
import { formatCurrency } from '../../utils/formatCurrency';


function AccountPage() {
  const navigate = useNavigate();
  const { settlementConfig } = useClient();
  const { setShowFooter } = useNav();
  setShowFooter(false);

  const { useGetAccount } = useAccount();
  const { useGetSfs, loadSfsData, sfsLoaded, disposableIncome } = useSfs();
  const {
    useGetArrangement,
    loadPlanData,
    planLoaded,
    lastSavedPlan,
  } = useArrangement();
  const { account, isAccountLoading } = useGetAccount();
  const { sfs, isSfsLoading } = useGetSfs();
  const { arrangement, isArrangementLoading } = useGetArrangement();

  const { useGetAccessibility } = useNotifications();
  const { accessibility, isAccessibilityLoading } = useGetAccessibility();

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();

  Date.prototype.loseDays = function (days) {
    var date = new Date(this.valueOf());
    date.setDate(date.getDate() - days);
    return date;
  }
  const now = new Date();
  console.log(`displayFrequency: ${clientConfig?.layout?.notifications?.cards?.one?.displayFrequency}`);
  const thresholdDateForRenderingNotificationPage = now.loseDays(clientConfig?.layout?.notifications?.cards?.one?.displayFrequency).toISOString()
  console.log(`thresholdDate: ${thresholdDateForRenderingNotificationPage}`);
  console.log(`Accessibility modified date: ${accessibility?.modified}`)

  useEffect(() => {
    if (clientConfig?.layout?.notifications?.showNotifications && clientConfig?.layout?.accessibility?.showAccessibilityModule && !isAccessibilityLoading && ((accessibility?.modified < thresholdDateForRenderingNotificationPage) || (accessibility?.modified === accessibility?.created)) && !(localStorage.getItem("remindMeLaterClicked") || localStorage.getItem("viewMyAccountClicked") || localStorage.getItem("startAccessibilityClicked") || localStorage.getItem("accessibilitySubmitted"))) {
      navigate("/notifications")
    }
  }, [isAccessibilityLoading])

  useEffect(() => {
    if (
      !isAccountLoading &&
      !isArrangementLoading &&
      !planLoaded &&
      sfsLoaded
    ) {
      loadPlanData(arrangement);
    }

    if (!sfsLoaded && !isSfsLoading) {
      loadSfsData(sfs);
    }
  }, [
    planLoaded,
    isSfsLoading,
    sfsLoaded,
    isArrangementLoading,
    loadPlanData,
    loadSfsData,
    arrangement,
    sfs,
  ]);

  if (
    isAccountLoading ||
    isSfsLoading ||
    isArrangementLoading ||
    isAccessibilityLoading
  ) {
    return <LinearProgress />;
  }

  return (
    <>
      <ScriptLoader />
      {account && planLoaded && sfsLoaded && !isAccessibilityLoading && (
        <>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Banner />
          </Box>
          <Container sx={{ mt: 4, mb: 7, paddingBottom: "350px" }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              divider={<Divider orientation='vertical' flexItem />}
              spacing={{ xs: 0.05, md: 4 }}
            >
              {/* Column 1 */}
              <Grid container direction={"column"} spacing={2} xs={12} md={6}>
                <BalanceWidget balance={account.currentBalance} />
                {clientConfig?.layout?.arrearsCard?.enabled &&
                  <Grid>
                    <ArrearsCard balance={formatCurrency(account.arrearsAmount)} paragraphOne={clientConfig?.layout?.arrearsCard?.paragraphs[0]} paragraphTwo={clientConfig?.layout?.arrearsCard?.paragraphs[1]} paragraphThree={clientConfig?.layout?.arrearsCard?.paragraphs[2]} paragraphFour={clientConfig?.layout?.arrearsCard?.paragraphs[3]} paragraphFive={clientConfig?.layout?.arrearsCard?.paragraphs[4]} tel={clientConfig?.layout?.arrearsCard?.tel} telFromAbroad={clientConfig?.layout?.arrearsCard?.telFromAbroad} ></ArrearsCard>
                  </Grid>}
                {!sfs ? (
                  <Grid>
                    <ActionLinkCard
                      id='income-expenditure-container'
                      backgroundColor='#FFF'
                      textColor='#000'
                      title={
                        <>
                          Complete your financial statement{" "}
                          <CircleIcon sx={{ color: "#EA4278", fontSize: "18px", verticalAlign: "top" }} />
                        </>
                      }
                      description='You currently don’t have an up-to-date Financial Statement on record. Please click the button below to get started.'
                      buttonId='complete-here-btn'
                      buttonColor='#FFF'
                      buttonTextColor='secondary.main'
                      buttonTitle='Start my financial statement'
                      hoverColor='secondary.dark'
                      hoverTextColor='#FFF'
                      target='/sfs'
                      icon={<AccountBalanceWalletIcon sx={{ color: "#000" }} />}
                    />
                  </Grid>
                ) : (
                  <Grid>
                    <ActionLinkCard
                      id='income-expenditure-container'
                      backgroundColor='#FFF'
                      textColor='#000'
                      title={"Your financial statement is up to date"}
                      disposableIncome={disposableIncome}
                      buttonId='complete-here-btn'
                      buttonColor='#FFF'
                      buttonTextColor='secondary.main'
                      buttonTitle='Change my financial statement'
                      hoverColor='secondary.main'
                      hoverTextColor='#FFF'
                      target='/sfs'
                      icon={<AccountBalanceWalletIcon sx={{ color: "#000" }} />}
                    />
                  </Grid>
                )}
              </Grid>
              {/* Column 2 */}
              <Grid container direction={"column"} spacing={2} xs={12} md={6}>
                {/* Show settlement offer if applicable, and current arrangement is not a settlement or pending payment */}
                {(settlementConfig?.required && !lastSavedPlan?.isSettlement && !lastSavedPlan?.inPaymentWindow) &&
                  <SettlementProvider>
                    <SettlementOffer balance={account?.currentBalance} />
                  </SettlementProvider>
                }
                {lastSavedPlan?.isSettlement ? (
                  <SettlementWidget />
                ) : (
                  <ArrangementWidget tel={clientConfig?.layout?.arrearsCard?.tel} telFromAbroad={clientConfig?.layout?.arrearsCard?.telFromAbroad}/>
                )}
              </Grid>
            </Stack>
          </Container>
          {setShowFooter(true)}
        </>
      )}
    </>
  );
}

export default AccountPage;
